// settingsUtils.js

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function isHex(value) {
  return typeof value === 'string' && HEX_COLOR_RE.test(value.trim());
}

function pickHex(override, fallback) {
  if (override === undefined || override === null || override === '') return fallback;
  return isHex(override) ? override.trim() : fallback;
}

/**
 * Hex-only theme builder (single source of truth).
 * Supports #RGB, #RRGGBB, #RRGGBBAA.
 */
export function buildThemeColors(accent, isDark, fontOverrides = {}) {
  const safeAccent = isHex(accent) ? accent.trim() : '#10B981';

  const base = isDark
    ? {
        accent: safeAccent,
        heading: '#FFFFFF',
        text: '#F8FAFC',
        muted: '#94A3B8',
        label: '#34D399',

        chatUserBg: safeAccent,
        chatUser: '#000000',
        chatAiBg: '#0D1411E6',
        chatAi: '#F8FAFC',

        aiButtonBg: safeAccent,
        aiButtonText: '#000000',

        pageBg: '#070A09',
        card: '#0D1411D9',
        border: '#10B98130',
        surface: '#111D17E6',
      }
    : {
        accent: safeAccent,
        heading: '#0F172A',
        text: '#1E293B',
        muted: '#64748B',
        label: safeAccent,

        chatUserBg: safeAccent,
        chatUser: '#FFFFFF',
        chatAiBg: '#F1F5F9',
        chatAi: '#1E293B',

        aiButtonBg: safeAccent,
        aiButtonText: '#FFFFFF',

        pageBg: '#F8FAFC',
        card: '#FFFFFFE6',
        border: '#E2E8F0',
        surface: '#F1F5F9',
      };

  return {
    accent: pickHex(fontOverrides.accent, base.accent),
    heading: pickHex(fontOverrides.heading, base.heading),
    text: pickHex(fontOverrides.text, base.text),
    muted: pickHex(fontOverrides.muted, base.muted),
    label: pickHex(fontOverrides.label, base.label),

    chatUserBg: pickHex(fontOverrides.chatUserBg, base.chatUserBg),
    chatUser: pickHex(fontOverrides.chatUser, base.chatUser),
    chatAiBg: pickHex(fontOverrides.chatAiBg, base.chatAiBg),
    chatAi: pickHex(fontOverrides.chatAi, base.chatAi),

    aiButtonBg: pickHex(fontOverrides.aiButtonBg, base.aiButtonBg),
    aiButtonText: pickHex(fontOverrides.aiButtonText, base.aiButtonText),

    pageBg: pickHex(fontOverrides.pageBg, base.pageBg),
    card: pickHex(fontOverrides.card, base.card),
    border: pickHex(fontOverrides.border, base.border),
    surface: pickHex(fontOverrides.surface, base.surface),

    _customFonts: !!fontOverrides._customFonts,
  };
}

/**
 * Extract only user-editable overrides.
 * If _customFonts is false, preserve only pageBg if explicitly set.
 */
export function extractFontOverrides(themeColors) {
  if (!themeColors) return {};

  const overrides = {};
  if (isHex(themeColors.pageBg)) overrides.pageBg = themeColors.pageBg;

  if (!themeColors._customFonts) return overrides;

  const keys = [
    'accent',
    'heading',
    'text',
    'muted',
    'label',
    'chatUserBg',
    'chatUser',
    'chatAiBg',
    'chatAi',
    'aiButtonBg',
    'aiButtonText',
    'pageBg',
    'card',
    'border',
    'surface',
  ];

  for (const k of keys) {
    if (isHex(themeColors[k])) overrides[k] = themeColors[k];
  }

  overrides._customFonts = true;
  return overrides;
}

export function getModalTheme(isDark) {
  return {
    isDark: Boolean(isDark),
    modalBg: isDark ? '#090E0C' : '#FFFFFF',
    modalBorder: isDark ? 'rgba(16, 185, 129, 0.25)' : '#E2E8F0',
    textColor: isDark ? '#F8FAFC' : '#0F172A',
    mutedText: isDark ? '#94A3B8' : '#64748B',
    cardBg: isDark ? '#0D1411' : '#F8FAFC',
    surfaceBg: isDark ? '#070A09' : '#F1F5F9',
    inputBg: isDark ? '#060807' : '#FFFFFF',
    inputBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#CBD5E1',
    labelColor: isDark ? '#34D399' : '#059669',
  };
}